import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { useTranslation } from 'react-i18next';

import { AppName, MediaPath } from '../config/Constants';
import SignOutButton from '../components/SignOutButton';
import useUserContext from '../config/UserContext';
import { btnDanger, btnMd } from '../styles/tw-components';

import type { StandardizedUser } from '../types/types';

const LaudLogo = `${MediaPath}Laud-Logo-Negative-2x.png`;

const stylesheet = {
    accountLink: 'tw:me-2 tw:text-white tw:no-underline tw:hover:text-gray-200',
    signInButton: `${btnDanger} ${btnMd} tw:text-white`,
    navbar: 'tw:fixed tw:top-0 tw:left-0 tw:right-0 tw:z-[1030] tw:bg-laud-purple tw:items-center tw:py-2 tw:pe-4 tw:w-auto',
    logo: 'tw:h-[32px] tw:w-[134px]',
    navRight: 'tw:ms-auto tw:flex tw:items-center tw:justify-end tw:gap-2',
    container: 'tw:w-full tw:px-4 tw:flex tw:flex-wrap tw:md:flex-nowrap tw:items-center tw:justify-between',
    toggleButton: 'tw:md:hidden tw:p-2 tw:text-white tw:bg-transparent tw:border tw:border-white/25 tw:rounded tw:cursor-pointer',
    collapsePanel: 'tw:md:flex tw:md:items-center tw:md:flex-grow tw:md:basis-auto tw:w-full tw:md:w-auto',
};

function renderUserData(context: StandardizedUser, t: (key: string, options?: Record<string, string>) => string) {
    if (context.type === 'unauthenticated') {
        const reload = () => window.location.assign('/signIn');
        return (
            <button
              type="button"
              className={stylesheet.signInButton}
              onClick={reload}
            >
                {t('common.buttons.signIn')}
            </button>
        );
    }

    let givenName = '';
    let familyName = '';
    let name = '';
    if (context.attributes) {
        if (context.attributes.given_name) {
            givenName = context.attributes.given_name;
        }
        if (context.attributes.family_name) {
            familyName = context.attributes.family_name;
        }
    }

    if (givenName === '' && familyName === '') {
        if (context.attributes?.email) {
            name = context.attributes.email;
        } else {
            name = t('nav.userFallback', { appName: AppName });
        }
    } else if (givenName || familyName) {
        name = `${givenName} ${familyName}`;
        name = name.trim();
    }
    return (
        <>
            {name && (
                <a
                  className={stylesheet.accountLink}
                  href="/account"
                >
                    {name}
                </a>
            )}
            <SignOutButton />
        </>
    );
}

function NavigationBar(/* props */) {
    const context = useUserContext();
    const { t } = useTranslation();
    return (
        <Disclosure as="nav" className={stylesheet.navbar}>
            <div className={stylesheet.container}>
                <a href="/">
                    <img
                      className={stylesheet.logo}
                      src={LaudLogo}
                      alt={AppName}
                      width={134}
                      height={32}
                      fetchPriority="high"
                    />
                </a>

                <DisclosureButton className={stylesheet.toggleButton} aria-label="Toggle navigation">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 30 30"
                      width="30"
                      height="30"
                      focusable="false"
                      aria-hidden="true"
                    >
                        <path
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeMiterlimit="10"
                          d="M4 7h22M4 15h22M4 23h22"
                        />
                    </svg>
                </DisclosureButton>

                <DisclosurePanel className={stylesheet.collapsePanel} static>
                    <div className={stylesheet.navRight}>
                        {renderUserData(context, t)}
                    </div>
                </DisclosurePanel>
            </div>
        </Disclosure>
    );
}

export default NavigationBar;
